import { type GridSize } from '@mui/material';
import { type ResponsiveStyleValue } from '@mui/system';
import { memo, type ReactNode } from 'react';

// TODO: Typing
// Filter, and SortKeys need to be typed, but not to keyof T, rather than actual type of the API request - just not sure how to do this yet

export const genericMemo: <T>(component: T) => T = memo;

export interface FilterOption {
  label: string;
  value: string | number;
}

export interface RangeValue {
  min?: number;
  max?: number;
}

interface BaseFilter {
  label: string;
  dataKey: string;
  hidden?: boolean;
}

export interface SelectFilter extends BaseFilter {
  type: 'select';
  options: FilterOption[];
  allowMultiple: boolean;
  isCountryField?: boolean;
}

export interface BooleanFilter extends BaseFilter {
  type: 'boolean';
}

export interface RangeFilter extends BaseFilter {
  type: 'range';
  options: (string | number)[];
}

export type Filter = SelectFilter | BooleanFilter | RangeFilter;

export type FilterValue = FilterOption[] | boolean | RangeValue | undefined;

export interface SortKey {
  label: string;
  dataKey: string;
  labelAsc?: string; // Default A - Z
  labelDesc?: string; // Default Z - A
}

export interface SearchParams {
  query: string;
  filters: Record<string, FilterValue>;
  sortBy: string | undefined;
  sortOrder: 'asc' | 'desc' | undefined;
}

export interface UseSearchReturn {
  // State
  query: string;
  page: number;
  sortBy: string | undefined;
  sortOrder: 'asc' | 'desc' | undefined;
  filterValues: Record<string, FilterValue>;

  // Setters
  setQuery: (query: string) => void;
  setFilterValues: (filterValues: Record<string, FilterValue>) => void;

  // Actions
  clearQuery: () => void;
  clearFilter: (dataKey: string, dataValue?: FilterOption) => void;
  clearSort: () => void;
  clearAllFilters: () => void;
  handleSortChange: (sortBy: string, sortOrder: 'asc' | 'desc') => void;
  handlePageChange: (page: number) => void;
}

export interface Props<T> {
  data: T[];
  dataLabel: string;
  onSearch: (params: SearchParams) => void;
  filters: Filter[];
  sortByKeys: SortKey[];
  totalCount: number;
  totalPages: number;
  pageSize: number;
  currentPageSize: number;
  renderItem: (item: T) => ReactNode;
  renderTopAction?: (searchState: UseSearchReturn) => ReactNode;
  gridSize?: ResponsiveStyleValue<GridSize>;
  isLoading: boolean;
  errorMessage?: string;
  debounceMs?: number;
}

export { default as SearchSkeleton } from './SearchSkeleton';
export { useSearch, useDebounce } from './hooks';
